
import { useMemo, useState, useEffect } from 'react';
import { Contact } from '@/hooks/useContacts';
import { ContactFilters } from '@/components/contacts/ContactsFilter';

const FILTER_STORAGE_KEY = 'contacts-filters';

export const useFilteredContacts = (contacts: Contact[]) => {
  // Initialize filters from localStorage or defaults
  const [filters, setFilters] = useState<ContactFilters>(() => {
    try {
      const saved = localStorage.getItem(FILTER_STORAGE_KEY);
      return saved ? JSON.parse(saved) : {
        name: '',
        email: '',
        companyName: '',
        designation: '',
        contactType: '',
        linkedBrandIds: [],
        createdAtFrom: '',
        createdAtTo: '',
      };
    } catch {
      return {
        name: '',
        email: '',
        companyName: '',
        designation: '',
        contactType: '',
        linkedBrandIds: [],
        createdAtFrom: '',
        createdAtTo: '',
      };
    }
  });

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  const filteredContacts = useMemo(() => {
    return contacts.filter(contact => {
      // Name filter (partial, case-insensitive)
      if (filters.name && !contact.name?.toLowerCase().includes(filters.name.toLowerCase())) {
        return false;
      }

      // Email filter (partial, case-insensitive)
      if (filters.email && !contact.email?.toLowerCase().includes(filters.email.toLowerCase())) {
        return false;
      }

      // Company name filter (partial, case-insensitive)
      if (filters.companyName && !contact.company_name?.toLowerCase().includes(filters.companyName.toLowerCase())) {
        return false;
      }

      // Designation filter (exact match, case-insensitive)
      if (filters.designation && contact.designation?.toLowerCase() !== filters.designation.toLowerCase()) {
        return false;
      }

      // Contact type filter (exact match)
      if (filters.contactType && contact.contact_type !== filters.contactType) {
        return false;
      }

      // Linked brand IDs filter (intersection)
      if (filters.linkedBrandIds.length > 0) {
        const contactBrandIds = contact.linked_brand_ids || [];
        const hasMatchingBrand = filters.linkedBrandIds.some(brandId => 
          contactBrandIds.includes(brandId)
        );
        if (!hasMatchingBrand) {
          return false;
        }
      }

      // Created date range filter
      if (filters.createdAtFrom || filters.createdAtTo) {
        const contactDate = new Date(contact.created_at).toISOString().split('T')[0];
        
        if (filters.createdAtFrom && contactDate < filters.createdAtFrom) {
          return false;
        }
        
        if (filters.createdAtTo && contactDate > filters.createdAtTo) {
          return false;
        }
      }

      return true;
    });
  }, [contacts, filters]);

  const clearFilters = () => {
    const emptyFilters: ContactFilters = {
      name: '',
      email: '',
      companyName: '',
      designation: '',
      contactType: '',
      linkedBrandIds: [],
      createdAtFrom: '',
      createdAtTo: '',
    };
    setFilters(emptyFilters);
  };

  const hasActiveFilters = useMemo(() => {
    return filters.name || 
           filters.email || 
           filters.companyName || 
           filters.designation || 
           filters.contactType || 
           filters.linkedBrandIds.length > 0 || 
           filters.createdAtFrom || 
           filters.createdAtTo;
  }, [filters]);

  return {
    filters,
    setFilters,
    filteredContacts,
    clearFilters,
    hasActiveFilters,
  };
};
